import React, { useState, forwardRef, useImperativeHandle } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";

const ImagePhoto = forwardRef(({ index, label, setImage, images, setImages, fileInputsRef }, ref) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [cropper, setCropper] = useState(null);
  const [error, setError] = useState("");
  const [oversizedFile, setOversizedFile] = useState(false);
  const [fileSelected, setFileSelected] = useState(false);

  // Expose reset function to parent
  useImperativeHandle(ref, () => ({
    resetFileFlags() {
      setFileSelected(false);
      setOversizedFile(false);
    },
  }));

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedFormats = ["image/jpeg", "image/jpg", "image/png"];
      const maxSizeMB = 2;

      if (!allowedFormats.includes(file.type)) {
        setError("Only JPG, JPEG, and PNG files are allowed.");
        setOversizedFile(false);
        return;
      }

      if (file.size > maxSizeMB * 1024 * 1024) {
        setError("Photo size up to 2MB only.");
        setOversizedFile(true);
        return;
      }

      setError("");
      setOversizedFile(false);
      setFileSelected(true);
      const reader = new FileReader();
      reader.onload = (event) => {
        setSelectedImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCrop = () => {
    if (cropper) {
      const croppedCanvas = cropper.getCroppedCanvas();
      if (croppedCanvas) {
        const roundedCanvas = document.createElement("canvas");
        const ctx = roundedCanvas.getContext("2d");

        roundedCanvas.width = croppedCanvas.width;
        roundedCanvas.height = croppedCanvas.height;

        ctx.beginPath();
        ctx.moveTo(20, 0);
        ctx.lineTo(croppedCanvas.width - 20, 0);
        ctx.quadraticCurveTo(croppedCanvas.width, 0, croppedCanvas.width, 20);
        ctx.lineTo(croppedCanvas.width, croppedCanvas.height - 20);
        ctx.quadraticCurveTo(croppedCanvas.width, croppedCanvas.height, croppedCanvas.width - 20, croppedCanvas.height);
        ctx.lineTo(20, croppedCanvas.height);
        ctx.quadraticCurveTo(0, croppedCanvas.height, 0, croppedCanvas.height - 20);
        ctx.lineTo(0, 20);
        ctx.quadraticCurveTo(0, 0, 20, 0);
        ctx.closePath();
        ctx.clip();

        ctx.drawImage(croppedCanvas, 0, 0);
        const roundedImage = roundedCanvas.toDataURL();

        setImages((prev) => {
          const updatedImages = [...prev];
          updatedImages[index] = roundedImage;
          return updatedImages;
        });
        setImage(roundedImage);
        setSelectedImage(null);
        setError("");
      } else {
        setError("Could not crop the image. Please try again.");
      }
    }
  };

  const handleCancelCrop = () => {
    setSelectedImage(null);
    setCropper(null);
    setError("");
  };

  const handleDelete = () => {
    setImages((prev) => {
      const updatedImages = [...prev];
      updatedImages[index] = null;
      return updatedImages;
    });
    if (fileInputsRef.current[index]) {
      fileInputsRef.current[index].value = "";
    }
    setSelectedImage(null);
    setFileSelected(false);
    setError("");
    setOversizedFile(false);
  };

  return (
    <div
      style={{
        border: "2px solid #ddd",
        borderRadius: "8px",
        padding: "15px",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "20px",
        position: "relative",
      }}
    >
      <div style={{ position: "relative" }}>
        <label style={{ color: "#3f51b5", fontWeight: "bold" }}>
          {label}:
          <input
            ref={(el) => (fileInputsRef.current[index] = el)}
            type="file"
            accept=".jpg,.jpeg,.png"
            onChange={handleImageUpload}
            style={{
              display: images[index] ? "none" : "block",
              marginTop: "10px",
              padding: "5px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontSize: "14px",
              cursor: "pointer",
            }}
          />
        </label>

        {(oversizedFile || (fileSelected && selectedImage)) && !images[index] && (
          <button
            onClick={handleDelete}
            style={{
              position: "absolute",
              top: "12px",
              right: "-8px",
              backgroundColor: "red",
              color: "white",
              border: "none",
              borderRadius: "50%",
              width: "18px",
              height: "18px",
              cursor: "pointer",
              fontSize: "12px",
              lineHeight: "18px",
              padding: "0",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            X
          </button>
        )}

        {selectedImage && !images[index] && (
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              padding: "10px",
              zIndex: 10,
              borderRadius: "10px",
            }}
          >
            <div
              style={{
                backgroundColor: "#fff",
                padding: "10px",
                borderRadius: "10px",
                maxWidth: "400px",
              }}
            >
              <Cropper
                src={selectedImage}
                style={{
                  height: "300px",
                  width: "100%",
                  borderRadius: "8px",
                  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.2)",
                }}
                aspectRatio={3 / 4}
                guides={false}
                viewMode={1}
                dragMode="move"
                scalable={true}
                zoomable={false}
                onInitialized={(instance) => setCropper(instance)}
              />
              <div style={{ marginTop: "10px", display: "flex", gap: "10px", justifyContent: "center" }}>
                <button
                  onClick={handleCrop}
                  style={{
                    backgroundColor: "#4CAF50",
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  Apply Crop
                </button>
                <button
                  onClick={handleCancelCrop}
                  style={{
                    backgroundColor: "#f44336",
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {images[index] && (
        <div style={{ marginTop: "10px", position: "relative" }}>
          <img
            src={images[index]}
            alt={`Uploaded ${index}`}
            style={{
              maxWidth: "100px",
              maxHeight: "100px",
              objectFit: "contain",
              border: "1px solid #ddd",
              borderRadius: "10px",
              display: "block",
              margin: "0 auto",
            }}
          />
          <button
            onClick={handleDelete}
            style={{
              position: "absolute",
              top: "-5px",
              right: "-5px",
              backgroundColor: "red",
              color: "white",
              border: "none",
              borderRadius: "50%",
              width: "20px",
              height: "20px",
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            X
          </button>
        </div>
      )}

      {error && (
        <div
          style={{
            position: "absolute",
            bottom: "0px",
            left: "0px",
            color: "red",
            fontSize: "11px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>{error}</span>
        </div>
      )}
    </div>
  );
});

export default ImagePhoto;
